import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, AtSign, Check, X, Loader2 } from 'lucide-react';
import { useResponsive } from '../hooks/useResponsive';
import { useAppState } from '../context/AppStateContext';
import { useAuth } from '../context/AuthContext';
import { bonndleAuthApi } from '../services/bonndleAuthApi';
import { getCountryCode } from '../utils/currency';

export default function CreateTagPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useResponsive();
  const { profileForm, setProfileForm } = useAppState();
  const { userProfile, fetchUserData } = useAuth();
  const inputRef = useRef(null);

  // Check signup flow type
  const socialSignup = location.state?.socialSignup;
  const phoneSignup = location.state?.phoneSignup;
  const pin = location.state?.pin; // PIN passed from CreatePinPage for phone signup
  const otp = location.state?.otp; // OTP code for backend verification
  const firebaseToken = location.state?.firebaseToken; // Firebase ID token (phoneVerificationToken)
  const otpId = location.state?.otpId; // Firebase verificationId or Termii pinId

  const [tagValue, setTagValue] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Check tag availability with debounce
  useEffect(() => {
    if (!tagValue || tagValue.length < 3) {
      setIsAvailable(null);
      setError('');
      return;
    }

    setIsChecking(true);
    setIsAvailable(null);

    const timer = setTimeout(async () => {
      try {
        // API returns { message: "Ok." } on success (tag available)
        // API throws 400 error with message "A user already exists with this bold tag." when taken
        await bonndleAuthApi.checkBoldTag(tagValue);
        setIsChecking(false);
        setIsAvailable(true);
        setError('');
      } catch (err) {
        console.log('[DEBUG] Tag check error:', err.message, err);
        setIsChecking(false);

        const errorMsg = (err.message || err.toString() || '').toLowerCase();

        // Check if it's a "tag already exists" error (400 status or message contains keywords)
        if (errorMsg.includes('already exists') ||
            errorMsg.includes('bold tag') ||
            errorMsg.includes('400')) {
          setIsAvailable(false);
          setError('This tag is already taken');
        } else {
          // Other errors - don't allow the tag
          setIsAvailable(false);
          setError('Unable to verify tag availability. Please try again.');
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [tagValue]);

  const handleChange = (e) => {
    let value = e.target.value.toLowerCase();
    // Remove @ if user types it, and only allow alphanumeric and underscore
    value = value.replace(/^@/, '').replace(/[^a-z0-9_]/g, '');
    setTagValue(value);
  };

  const isValid = tagValue.length >= 3 && tagValue.length <= 20 && isAvailable === true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      if (phoneSignup && pin) {
        // For phone signup, register with all collected data
        console.log('[DEBUG] Phone signup - registering user with:', {
          phone: userProfile?.phone,
          firstName: userProfile?.firstName,
          lastName: userProfile?.lastName,
          country: userProfile?.country,
          boldTag: tagValue,
          referralCode: userProfile?.referralCode,
          hasPhoneVerificationToken: !!firebaseToken,
          hasOtpId: !!otpId,
          hasOtp: !!otp,
        });

        // Check if phone number already exists
        try {
          const phoneCheck = await bonndleAuthApi.checkPhone(userProfile?.phone);
          if (phoneCheck.exists) {
            throw new Error('This phone number is already registered. Please sign in instead.');
          }
        } catch (checkErr) {
          // If checkPhone fails with an error about existing phone, show that message
          if (checkErr.message?.includes('already') || checkErr.message?.includes('exists')) {
            throw checkErr;
          }
          // Otherwise continue with registration (checkPhone might not be implemented)
          console.log('[DEBUG] Phone check skipped:', checkErr.message);
        }

        // Debug: Log userProfile before registration
        console.log('[DEBUG] CreateTagPage userProfile before register:', {
          phone: userProfile?.phone,
          firstName: userProfile?.firstName,
          lastName: userProfile?.lastName,
          country: userProfile?.country,
          referralCode: userProfile?.referralCode,
        });

        const registerResult = await bonndleAuthApi.register({
          phone: userProfile?.phone,
          firstName: userProfile?.firstName,
          lastName: userProfile?.lastName,
          country: userProfile?.country || 'Nigeria',
          boldTag: tagValue,
          pin: pin,
          referralCode: userProfile?.referralCode,
          // Phone verification fields for backend
          phoneVerificationToken: firebaseToken,
          otpId: otpId,
          otp: otp,
        });

        // Fetch user data to populate AuthContext user state
        // This ensures Menu, Profile, and Home pages show correct data
        try {
          const userData = await fetchUserData();
          console.log('[CreateTagPage] Fetched user data after registration:', userData?.profile?.first_name);

          // Sync profileForm with fetched user data
          if (userData?.profile) {
            const profile = userData.profile;
            setProfileForm({
              ...profileForm,
              firstName: profile.first_name || profile.firstName || userProfile?.firstName || '',
              lastName: profile.last_name || profile.lastName || userProfile?.lastName || '',
              email: profile.email || '',
              phone: profile.phone || profile.phone_number || userProfile?.phone || '',
              country: getCountryCode(profile.country) || userProfile?.country || '',
              bonndleTag: profile.bold_tag || profile.boldTag || tagValue,
            });
          }
        } catch (fetchErr) {
          console.warn('[CreateTagPage] Failed to fetch user data, using registration response:', fetchErr);
          // Fallback: use registration response if available
          if (registerResult?.user) {
            const user = registerResult.user;
            setProfileForm({
              ...profileForm,
              firstName: user.first_name || user.firstName || userProfile?.firstName || '',
              lastName: user.last_name || user.lastName || userProfile?.lastName || '',
              email: user.email || '',
              phone: user.phone || user.phone_number || userProfile?.phone || '',
              country: getCountryCode(user.country) || userProfile?.country || '',
              bonndleTag: user.bold_tag || user.boldTag || tagValue,
            });
          } else {
            // Last resort: use userProfile from signup flow
            setProfileForm({
              ...profileForm,
              firstName: userProfile?.firstName || '',
              lastName: userProfile?.lastName || '',
              phone: userProfile?.phone || '',
              country: userProfile?.country || '',
              bonndleTag: tagValue,
            });
          }
        }

        // Clear signup profile from sessionStorage now that registration is complete
        try {
          sessionStorage.removeItem('bonndle:signup_profile');
        } catch (e) {
          // Ignore
        }

        // Navigate to success page
        navigate('/signup-success', {
          state: { phoneSignup: true }
        });
      } else {
        // For social signup or other flows, store tag via existing API
        await bonndleAuthApi.storeBoldTag(tagValue);

        // Fetch user data to populate AuthContext user state
        try {
          const userData = await fetchUserData();
          console.log('[CreateTagPage] Fetched user data after social signup:', userData?.profile?.first_name);

          // Sync profileForm with fetched user data
          if (userData?.profile) {
            const profile = userData.profile;
            setProfileForm({
              ...profileForm,
              firstName: profile.first_name || profile.firstName || '',
              lastName: profile.last_name || profile.lastName || '',
              email: profile.email || '',
              phone: profile.phone || profile.phone_number || '',
              country: getCountryCode(profile.country) || '',
              bonndleTag: profile.bold_tag || profile.boldTag || tagValue,
            });
          }
        } catch (fetchErr) {
          console.warn('[CreateTagPage] Failed to fetch user data for social signup:', fetchErr);
          // Fallback: just save the tag
          setProfileForm({
            ...profileForm,
            bonndleTag: tagValue,
          });
        }

        // Navigate to success page
        navigate('/signup-success', {
          state: { socialSignup, phoneSignup }
        });
      }
    } catch (err) {
      console.error('Failed to complete signup:', err);
      setError(err.message || 'Failed to complete signup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/create-pin');
  };

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 bg-white z-10 flex items-center gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-gray-100">
          <button
            onClick={handleBack}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">Create An Account</h1>
        </header>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 px-6 py-8 flex flex-col">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AtSign className="text-indigo-600" size={28} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Choose your Bonndle tag</h2>
            <p className="text-gray-600">
              Your unique tag is how others find and mention you. It starts with @
            </p>
          </div>

          {/* Tag Input */}
          <div className="mb-4">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-indigo-600">
                @
              </span>
              <input
                ref={inputRef}
                type="text"
                value={tagValue}
                onChange={handleChange}
                placeholder="yourname"
                maxLength={20}
                className={`w-full pl-10 pr-12 py-4 text-xl font-semibold border-2 rounded-2xl focus:outline-none transition-colors ${
                  error
                    ? 'border-red-500 focus:border-red-500'
                    : isAvailable
                    ? 'border-emerald-500 focus:border-emerald-500'
                    : 'border-gray-200 focus:border-indigo-500'
                }`}
              />
              {/* Status indicator */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {isChecking && (
                  <Loader2 size={20} className="text-gray-400 animate-spin" />
                )}
                {!isChecking && isAvailable === true && (
                  <Check size={20} className="text-emerald-500" />
                )}
                {!isChecking && isAvailable === false && (
                  <X size={20} className="text-red-500" />
                )}
              </div>
            </div>

            {/* Helper text */}
            <div className="mt-2 px-1">
              {error ? (
                <p className="text-sm text-red-500">{error}</p>
              ) : tagValue.length > 0 && tagValue.length < 3 ? (
                <p className="text-sm text-gray-500">Minimum 3 characters</p>
              ) : isAvailable ? (
                <p className="text-sm text-emerald-600">@{tagValue} is available!</p>
              ) : (
                <p className="text-sm text-gray-500">Letters, numbers, and underscores only</p>
              )}
            </div>
          </div>

          {/* Preview */}
          {tagValue.length >= 3 && isAvailable && (
            <div className="bg-gray-50 rounded-2xl p-4 mb-6">
              <p className="text-sm text-gray-500 mb-2">Your profile will show:</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {tagValue.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-gray-900">
                    {userProfile?.firstName || profileForm.firstName || 'Your Name'}
                  </p>
                  <p className="text-indigo-600 font-semibold">@{tagValue}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1" />

          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition-colors ${
              isValid && !isSubmitting
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
